'use client'

import Image from 'next/image'
import {FC} from 'react'

import {BreadcrumbStep} from '@/app/lib/structuredData'
import {GetStartedWithVerificationBanner} from '@/components/common/banner/GetStartedWithVerificationBanner'
import {Breadcrumbs} from '@/components/common/content/Breadcrumbs'
import {ListItemWithCheckmark} from '@/components/common/content/ListItemWithCheckmark'
import {Section} from '@/components/common/content/Section'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {Hero} from '@/components/common/hero/Hero'
import {usePreferences} from '@/components/context/Preferences'
import {CountryGuideCardList} from '@/components/country-guide/CountryGuideCardList'
import {CountryGuideContentLayout} from '@/components/country-guide/CountryGuideContentLayout'
import {Page} from '@/components/Page'
import {getCountryGuideContent} from '@/content/countryGuide'
import {useHomepageContent} from '@/content/homepage'
import {Trans, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {CountryGuideBasicInfo, CountryGuideContent} from '@/utils/contentful'
import {Jurisdiction, NotOfferedJurisdiction} from '@/utils/countries/constants'
import {alpha2ToName} from '@/utils/countries/translations'
import {Currency, jurisdictionToCurrency} from '@/utils/currency'
import {useLanguage} from '@/utils/i18n/language'
import {formatPrice} from '@/utils/pricing'
import {routeTo} from '@/utils/router'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

const POPULAR_COUNTRIES_OFFSET = 4

type CountryGuideSpecificCountryProps = {
  mostPopularCountries: CountryGuideBasicInfo[]
  countrySpecificGuide: CountryGuideContent
  offeringPrices: Record<Currency, number> | null
  breadcrumbTrail: BreadcrumbStep[]
}

const CountryGuideSpecificCountry: FC<CountryGuideSpecificCountryProps> = ({
  mostPopularCountries,
  countrySpecificGuide,
  offeringPrices,
  breadcrumbTrail,
}) => {
  const {t} = useTranslation()
  const language = useLanguage()
  const {currency, isPreferencesPending} = usePreferences()
  const {getStartedBanner} = useHomepageContent()
  const {
    otherPopularCountriesTitle,
    countrySpecific: {hero},
  } = getCountryGuideContent()
  const {countryCode, subtitle, averageRegistrationTime, applicationsFiledPerYear, heroImage} =
    countrySpecificGuide

  const otherPopularCountries = mostPopularCountries
    .filter((country) => country.countryCode !== countryCode && !!country.homeCardOrder)
    .slice(0, POPULAR_COUNTRIES_OFFSET)

  const isOfferedJurisdiction = Object.values(Jurisdiction).includes(countryCode as Jurisdiction)
  const homeCurrency = jurisdictionToCurrency(countryCode)
  const price = offeringPrices?.[homeCurrency]
  // Quoted in the jurisdiction's currency, with the visitor's added in brackets once it is known.
  const visitorPrice =
    isPreferencesPending || currency === homeCurrency ? undefined : offeringPrices?.[currency]

  const benefits = [
    ...(averageRegistrationTime
      ? [{title: hero.averageRegistrationTimeTitle, value: averageRegistrationTime}]
      : []),
    ...(applicationsFiledPerYear
      ? [{title: hero.applicationsFiledPerYearTitle, value: applicationsFiledPerYear}]
      : []),
    ...(price
      ? [
          {
            title: hero.registrationPriceTitle,
            value:
              formatPrice(price, homeCurrency, language, false) +
              (visitorPrice ? ` (${formatPrice(visitorPrice, currency, language, false)})` : ''),
            tooltipText: hero.registrationPriceTooltip,
          },
        ]
      : []),
  ]

  const primaryButtonLinkHref = isOfferedJurisdiction
    ? routeTo.verification.form({country: countryCode.toLowerCase()})
    : routeTo.verification.form()

  const isWIPO = countryCode === NotOfferedJurisdiction.WO

  const secondaryButton = isWIPO
    ? {text: t(hero.priceEstimateButtonText), linkHref: routeTo.wipoCalculator()}
    : {text: t(hero.secondaryButtonText), linkHref: routeTo.pricing()}

  return (
    <>
      <Page>
        <Hero
          title={
            <Trans
              i18nKey={hero.title}
              values={{jurisdiction: alpha2ToName(countryCode, language)}}
              components={{
                1: (
                  <Text variant={TextVariant.h1} color={theme.palette.primary.main} element="span">
                    {alpha2ToName(countryCode, language)}
                  </Text>
                ),
              }}
            />
          }
          subtitle={subtitle}
          primaryButtonLink={primaryButtonLinkHref}
          primaryButtonText={t(hero.primaryButtonText)}
          primaryButtonTrackedEvent={`Click on primary button in Hero - Country guide (${countryCode})`}
          secondaryButtonLink={secondaryButton.linkHref}
          secondaryButtonText={secondaryButton.text}
          secondaryButtonTrackedEvent={`Click on secondary button in Hero - Country guide (${countryCode})`}
          benefits={
            benefits.length > 0 && (
              <ul className="list">
                {benefits.map(({title, value, tooltipText}, i) => (
                  <ListItemWithCheckmark
                    key={i}
                    text={<Trans i18nKey={title} values={{value}} />}
                    fontWeight={TextFontWeight.medium}
                    color={theme.palette.text.light}
                    tooltipText={t(tooltipText)}
                    verticalAlign="middle"
                  />
                ))}
              </ul>
            )
          }
          alignItems="center"
          breadcrumbs={
            <Breadcrumbs
              trail={breadcrumbTrail}
              // The country is named in the h1 directly below.
              hideCurrent
            />
          }
        >
          {heroImage?.url && (
            <div className="image">
              <Image
                src={heroImage.url}
                alt={alpha2ToName(countryCode, language)}
                sizes={`${MOBILE_SCREENSIZE} 315px, 535px`}
                fill
                priority
                style={{objectFit: 'contain'}}
              />
            </div>
          )}
        </Hero>
        <Section hasPaddingTop>
          <CountryGuideContentLayout countrySpecificGuide={countrySpecificGuide} />
        </Section>
        <Section hasPaddingTop>
          <CountryGuideCardList
            title={t(otherPopularCountriesTitle)}
            cards={otherPopularCountries}
            numOfCardsPerRow={otherPopularCountries.length}
            showDescription={false}
          />
        </Section>
        <GetStartedWithVerificationBanner
          title={t(getStartedBanner.title)}
          primaryButtonLink={primaryButtonLinkHref}
          primaryButtonText={t(getStartedBanner.primaryButtonText)}
          primaryButtonTrackedEvent={`GetStartedBanner-CountryGuide${countryCode}`}
          hasPaddingTop
        />
      </Page>

      <style jsx>{`
        .image {
          position: relative;
          width: 535px;
          height: 500px;
          margin: -${theme.spacing[2.5]} -${theme.spacing[5]} -${theme.spacing[2.5]} ${theme.spacing[5.5]};
        }
        .list {
          margin-top: ${theme.spacing[3.5]};
        }
        @media ${TABLET_SCREENSIZE} {
          .image {
            margin-left: 0;
            margin-top: ${theme.spacing[6]};
          }
          .list {
            display: inline-flex;
            flex-direction: column;
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .image {
            width: 315px;
            height: 280px;
            margin: ${theme.spacing[6]} 0 0;
          }
        }
      `}</style>
    </>
  )
}

export default CountryGuideSpecificCountry
